/** Convert browser Git hosting URLs into clone-ready remotes. */
export function normalizeGitRemoteUrl(rawUrl: string, branch = ''): { url: string; branch: string } {
  const trimmed = rawUrl.trim();
  let branchOut = branch.trim();
  if (!trimmed) {
    return { url: trimmed, branch: branchOut || 'main' };
  }
  if (trimmed.startsWith('git@')) {
    return { url: trimmed, branch: branchOut || 'main' };
  }

  try {
    const parsed = new URL(trimmed.replace(/\/$/, ''));
    const host = parsed.hostname.toLowerCase();
    const parts = parsed.pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);

    if (host.includes('github.com') && parts.length >= 2) {
      const owner = parts[0];
      const repo = parts[1].replace(/\.git$/i, '');
      if (parts.length >= 4 && (parts[2] === 'tree' || parts[2] === 'blob') && parts[3]) {
        branchOut = pickBranch(branchOut, parts[3]);
      }
      return {
        url: `${parsed.protocol}//${parsed.host}/${owner}/${repo}.git`,
        branch: branchOut || 'main',
      };
    }

    if (host.includes('gitlab')) {
      const marker = '/-/';
      const fullPath = parsed.pathname;
      const markerIdx = fullPath.indexOf(marker);
      if (markerIdx >= 0) {
        const projectPath = fullPath.slice(1, markerIdx).replace(/\/$/, '');
        const rest = fullPath.slice(markerIdx + marker.length).replace(/^\/+|\/+$/g, '').split('/');
        if (rest.length >= 2 && (rest[0] === 'tree' || rest[0] === 'blob') && rest[1]) {
          branchOut = pickBranch(branchOut, rest[1]);
        }
        if (projectPath) {
          return {
            url: `${parsed.protocol}//${parsed.host}/${projectPath}.git`,
            branch: branchOut || 'main',
          };
        }
      }
    }

    if (host.includes('bitbucket.org') && parts.length >= 2) {
      if (parts.length >= 4 && parts[2] === 'src' && parts[3]) {
        branchOut = pickBranch(branchOut, parts[3]);
      }
      return {
        url: `${parsed.protocol}//${parsed.host}/${parts[0]}/${parts[1].replace(/\.git$/i, '')}.git`,
        branch: branchOut || 'main',
      };
    }

    const base = `${parsed.protocol}//${parsed.host}${parsed.pathname.replace(/\/$/, '')}`;
    return {
      url: base.toLowerCase().endsWith('.git') ? base : `${base}.git`,
      branch: branchOut || 'main',
    };
  } catch {
    return { url: trimmed, branch: branchOut || 'main' };
  }
}

function pickBranch(current: string, fromUrl: string): string {
  if (!current || current === 'main' || current === 'master') {
    return fromUrl;
  }
  return current;
}

export function normalizeRepoSpecs<T extends { url: string; branch?: string }>(repos: T[]): T[] {
  return repos.map((repo) => {
    const url = repo.url.trim();
    if (!url) return repo;
    const normalized = normalizeGitRemoteUrl(url, repo.branch ?? '');
    return { ...repo, url: normalized.url, branch: normalized.branch };
  });
}
